import React from 'react';
import { motion } from 'framer-motion';
import { ZapIcon, CalendarIcon, ActivityIcon, ArrowRightIcon } from 'lucide-react';
export function TreatmentFrequency() {
  return <section className="py-20 md:py-28 bg-white" data-id="element-201">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-202">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-203">
          <h2 className="text-3xl md:text-5xl font-bold text-brand-navy mb-6" data-id="element-204">
            How Many Treatments Are Usually Recommended?
          </h2>
          <p className="text-lg text-gray-600" data-id="element-205">
            Laser therapy effects are cumulative. Every patient is unique, and
            treatment plans are customized based on the severity and duration of
            the condition.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-206">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-brand-gray-light rounded-2xl p-8 border border-gray-200" data-id="element-207">
            <div className="flex items-center gap-4 mb-6" data-id="element-208">
              <div className="w-12 h-12 bg-brand-teal/10 rounded-lg flex items-center justify-center text-brand-teal" data-id="element-209">
                <ZapIcon className="w-6 h-6" data-id="element-210" />
              </div>
              <h3 className="text-2xl font-bold text-brand-navy" data-id="element-211">
                Acute Conditions
              </h3>
            </div>
            <p className="text-gray-600 mb-4 leading-relaxed" data-id="element-212">
              Recent injuries, sudden flare-ups, or post-surgical recovery.
            </p>
            <ul className="space-y-3 text-gray-700 font-medium" data-id="element-213">
              <li className="flex items-center gap-2" data-id="element-214">
                <div className="w-2 h-2 rounded-full bg-brand-teal" data-id="element-215" />
                Often improve in fewer visits (e.g., 4 to 6 sessions)
              </li>
              <li className="flex items-center gap-2" data-id="element-216">
                <div className="w-2 h-2 rounded-full bg-brand-teal" data-id="element-217" />
                Faster initial response is common
              </li>
              <li className="flex items-center gap-2" data-id="element-218">
                <div className="w-2 h-2 rounded-full bg-brand-teal" data-id="element-219" />
                Treatments may be clustered closely together
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="bg-brand-navy rounded-2xl p-8 border border-brand-navy" data-id="element-220">
            <div className="flex items-center gap-4 mb-6" data-id="element-221">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center text-teal-300" data-id="element-222">
                <ActivityIcon className="w-6 h-6" data-id="element-223" />
              </div>
              <h3 className="text-2xl font-bold text-white" data-id="element-224">
                Chronic Conditions
              </h3>
            </div>
            <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-225">
              Long-standing pain, neuropathy, or severe arthritis.
            </p>
            <ul className="space-y-3 text-gray-200 font-medium" data-id="element-226">
              <li className="flex items-center gap-2" data-id="element-227">
                <div className="w-2 h-2 rounded-full bg-teal-400" data-id="element-228" />
                Often need a longer treatment series (e.g., 6 to 12+ sessions)
              </li>
              <li className="flex items-center gap-2" data-id="element-229">
                <div className="w-2 h-2 rounded-full bg-teal-400" data-id="element-230" />
                Improvement is typically gradual and cumulative
              </li>
              <li className="flex items-center gap-2" data-id="element-231">
                <div className="w-2 h-2 rounded-full bg-teal-400" data-id="element-232" />
                May require ongoing maintenance visits
              </li>
            </ul>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="mb-16" data-id="element-233">
          <h4 className="text-center text-xl font-bold text-brand-navy mb-8" data-id="element-234">
            A Common Treatment Rhythm Example
          </h4>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6" data-id="element-235">
            <div className="bg-white border-2 border-brand-teal rounded-lg px-6 py-4 text-center w-full md:w-auto" data-id="element-236">
              <span className="block text-sm text-gray-500 font-bold uppercase tracking-wider mb-1" data-id="element-237">
                Week 1
              </span>
              <span className="text-xl font-bold text-brand-navy" data-id="element-238">
                3 Visits
              </span>
            </div>
            <ArrowRightIcon className="w-6 h-6 text-gray-300 hidden md:block" data-id="element-239" />
            <div className="bg-white border-2 border-brand-teal/60 rounded-lg px-6 py-4 text-center w-full md:w-auto" data-id="element-240">
              <span className="block text-sm text-gray-500 font-bold uppercase tracking-wider mb-1" data-id="element-241">
                Week 2
              </span>
              <span className="text-xl font-bold text-brand-navy" data-id="element-242">
                2 Visits
              </span>
            </div>
            <ArrowRightIcon className="w-6 h-6 text-gray-300 hidden md:block" data-id="element-243" />
            <div className="bg-white border-2 border-brand-teal/30 rounded-lg px-6 py-4 text-center w-full md:w-auto" data-id="element-244">
              <span className="block text-sm text-gray-500 font-bold uppercase tracking-wider mb-1" data-id="element-245">
                Weeks 3+
              </span>
              <span className="text-xl font-bold text-brand-navy" data-id="element-246">Weekly</span>
            </div>
            <ArrowRightIcon className="w-6 h-6 text-gray-300 hidden md:block" data-id="element-247" />
            <div className="bg-brand-gray-light border-2 border-gray-200 rounded-lg px-6 py-4 text-center w-full md:w-auto" data-id="element-248">
              <span className="block text-sm text-gray-500 font-bold uppercase tracking-wider mb-1" data-id="element-249">
                Ongoing
              </span>
              <span className="text-xl font-bold text-gray-700" data-id="element-250">
                Maintenance
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="text-center" data-id="element-251">
          <p className="text-sm text-gray-500 italic max-w-3xl mx-auto mb-8" data-id="element-252">
            * These are common examples, not guarantees. Treatment frequency
            depends entirely on the patient, condition, severity, and clinical
            judgment of the provider. Some patients choose wellness or
            maintenance follow-up visits after the initial plan is completed.
          </p>
          <button className="px-8 py-4 bg-brand-teal hover:bg-brand-teal-dark text-white rounded-lg font-semibold text-lg transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-lg shadow-brand-teal/25" data-id="element-253">
            <CalendarIcon className="w-5 h-5" data-id="element-254" />
            Discuss Your Treatment Plan
          </button>
        </motion.div>
      </div>
    </section>;
}